/**
 * ChartSection — reusable wrapper for pulse dashboard chart panels.
 * Provides consistent card styling, section header, optional per-chart filters,
 * and fullscreen expand via a Modal.
 */

import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Modal, Typography, Tooltip, theme } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ChartSectionProps {
  title: string;
  subtitle?: string;
  color?: string;
  /** Optional controls rendered to the right of the title (e.g. Select dropdowns) */
  headerExtra?: React.ReactNode;
  /** Content at normal (compact) height */
  children: React.ReactNode;
  /** If provided, rendered inside fullscreen modal instead of children */
  fullscreenContent?: React.ReactNode;
  /** Override modal width (default 960) */
  fullscreenWidth?: number;
  /** Additional flex styles for the outer container */
  style?: React.CSSProperties;
}

export function ChartSection({
  title,
  subtitle,
  color = '#3b82f6',
  headerExtra,
  children,
  fullscreenContent,
  fullscreenWidth = 960,
  style,
}: ChartSectionProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div
        style={{
          background: token.colorBgContainer,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: '16px 20px',
          overflow: 'hidden',
          ...style,
        }}
      >
        {/* Title row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 5,
                height: subtitle ? 32 : 20,
                borderRadius: 3,
                flexShrink: 0,
                background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: 14 }}>{title}</Text>
                {headerExtra}
              </div>
              {subtitle && (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>{subtitle}</Text>
                </div>
              )}
            </div>
          </div>

          <Tooltip title={t('common.expand')}>
            <div
              onClick={() => setExpanded(true)}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorBgContainer,
                transition: 'all 0.15s ease',
                flexShrink: 0,
                marginTop: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = color;
                e.currentTarget.style.color = color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = token.colorBorderSecondary;
                e.currentTarget.style.color = '';
              }}
            >
              <ExpandOutlined style={{ fontSize: 12 }} />
            </div>
          </Tooltip>
        </div>

        {/* Chart content */}
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          {children}
        </div>
      </div>

      {/* Fullscreen modal */}
      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        footer={null}
        width={fullscreenWidth}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 5,
                height: 20,
                borderRadius: 3,
                background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
              }}
            />
            <span>{title}</span>
          </div>
        }
        wrapClassName="chart-fullscreen-modal"
        destroyOnClose
      >
        <div style={{ padding: '8px 0' }}>
          {fullscreenContent ?? children}
        </div>
      </Modal>
    </>
  );
}
